// src/components/Navbar.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './Navbar.css';

const Navbar = () => {
  const [dropdown, setDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const timeoutRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close mobile menu on resize to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleMouseEnter = (menu) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (window.innerWidth > 1024) {
      setDropdown(menu);
    }
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      if (window.innerWidth > 1024) {
        setDropdown(null);
      }
    }, 200);
  };

  const handleLinkClick = () => {
    setDropdown(null);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const toggleMobileDropdown = (menu) => {
    if (window.innerWidth <= 1024) {
      setDropdown(dropdown === menu ? null : menu);
    }
  };

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <div className="navbar-container">
        {/* Logo - Using ABVIG Image */}
        <Link to="/" className="navbar-logo" onClick={handleLinkClick}>
          <div className="logo-icon">
            <img 
              src="/images/hero/abvig-logo2.png" 
              alt="ABVIG" 
              className="navbar-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <span className="logo-text-location"></span>
          </div>
        </Link>

        {/* Desktop Register Button - Goes to /register */}
        <Link to="/register" className="register-btn">
          Register Now
        </Link>

        {/* Hamburger Menu Button */}
        <button 
          className={`hamburger-btn ${mobileMenuOpen ? 'active' : ''}`}
          onClick={toggleMobileMenu}
          aria-label="Toggle menu"
        >
          <span></span>
          <span></span>
          <span></span>
        </button>

        {/* Navigation Menu */}
        <ul className={`nav-menu ${mobileMenuOpen ? 'active' : ''}`}>
          {/* Home */}
          <li className="nav-item">
            <Link to="/" className="nav-link" onClick={handleLinkClick}>
              <span className="link-text">Home</span>
              <span className="link-underline"></span>
            </Link>
          </li>

          {/* AICIS Events Dropdown */}
          <li 
            className="nav-item dropdown"
            onMouseEnter={() => handleMouseEnter('events')}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              className="nav-link dropdown-toggle"
              onClick={() => toggleMobileDropdown('events')}
            >
              <span className="link-text">AICIS Events</span>
              <svg className="dropdown-arrow" viewBox="0 0 24 24" width="16" height="16">
                <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
              <span className="link-underline"></span>
            </button>
            <div className={`dropdown-menu ${dropdown === 'events' ? 'active' : ''}`}>
              <div className="dropdown-inner">
                <Link to="/events/about" onClick={handleLinkClick}>
                  <span className="dropdown-icon">ℹ️</span>
                  About AICIS
                </Link>
                {/* <Link to="/events/aicis-2024" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🇺🇸</span>
                  AICIS 2024 (USA)
                </Link> */}
                <Link to="/events/aicic-2025" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🇳🇬</span>
                  AICIS 2025 (Abuja, Nigeria)
                </Link>
                <Link to="/events/pre-summit" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🔄</span>
                  Diplomatic Round Table 2026
                </Link>
                <Link to="/events/global-summit" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🌍</span>
                  2026 Global Summit
                </Link>
                <Link to="/activities/speakers" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🎤</span>
                  Speakers
                </Link>
              </div>
            </div>
          </li>

          {/* Affiliate */}
          <li className="nav-item">
            <Link to="/affiliate" className="nav-link" onClick={handleLinkClick}>
              <span className="link-text">Affiliate</span>
              <span className="link-underline"></span>
            </Link>
          </li>

          {/* Activities Dropdown */}
          <li 
            className="nav-item dropdown"
            onMouseEnter={() => handleMouseEnter('activities')}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              className="nav-link dropdown-toggle"
              onClick={() => toggleMobileDropdown('activities')}
            >
              <span className="link-text">Activities</span>
              <svg className="dropdown-arrow" viewBox="0 0 24 24" width="16" height="16">
                <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
              <span className="link-underline"></span>
            </button>
            <div className={`dropdown-menu dropdown-wide ${dropdown === 'activities' ? 'active' : ''}`}>
              <div className="dropdown-inner grid-2">
                <Link to="/activities/strategic-alignment" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🎯</span>
                  Strategic Alignment
                </Link>
                <Link to="/activities/host-country" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🏛️</span>
                  Host Country Profile
                </Link>
                <Link to="/activities/objectives" onClick={handleLinkClick}>
                  <span className="dropdown-icon">⭐</span>
                  Objectives
                </Link>
                <Link to="/activities/invited-heads" onClick={handleLinkClick}>
                  <span className="dropdown-icon">👥</span>
                  Invited Heads & VIPs
                </Link>
                <Link to="/activities/speakers" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🎤</span>
                  Speakers
                </Link>
                <Link to="/activities/schedule" onClick={handleLinkClick}>
                  <span className="dropdown-icon">📅</span>
                  Schedule
                </Link>
              </div>
            </div>
          </li>

          {/* Resources Dropdown */}
          <li 
            className="nav-item dropdown"
            onMouseEnter={() => handleMouseEnter('resources')}
            onMouseLeave={handleMouseLeave}
          >
            <button 
              className="nav-link dropdown-toggle"
              onClick={() => toggleMobileDropdown('resources')}
            >
              <span className="link-text">Resources</span>
              <svg className="dropdown-arrow" viewBox="0 0 24 24" width="16" height="16">
                <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="2" fill="none"/>
              </svg>
              <span className="link-underline"></span>
            </button>
            <div className={`dropdown-menu ${dropdown === 'resources' ? 'active' : ''}`}>
              <div className="dropdown-inner">
                <Link to="/resources/brochures" onClick={handleLinkClick}>
                  <span className="dropdown-icon">📄</span>
                  Brochures
                </Link>
                <Link to="/resources/jingle" onClick={handleLinkClick}>
                  <span className="dropdown-icon">🎵</span>
                  Jingle
                </Link>
              </div>
            </div>
          </li>

          {/* Policy */}
          <li className="nav-item">
            <Link to="/policy" className="nav-link" onClick={handleLinkClick}>
              <span className="link-text">Policy</span>
              <span className="link-underline"></span>
            </Link>
          </li>

          {/* FAQ */}
          <li className="nav-item">
            <Link to="/faq" className="nav-link" onClick={handleLinkClick}>
              <span className="link-text">FAQ</span>
              <span className="link-underline"></span>
            </Link>
          </li>

          {/* Blog */}
          <li className="nav-item">
            <Link to="/blog" className="nav-link" onClick={handleLinkClick}>
              <span className="link-text">Blog</span>
              <span className="link-underline"></span>
            </Link>
          </li>

          {/* Mobile Register Button - Goes to /register */}
          <li className="mobile-register-btn">
            <Link to="/register" onClick={handleLinkClick}>
              Register Now
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
};

export default Navbar;